"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import * as Tabs from "@radix-ui/react-tabs";

import type {
  IMentorLesson,
  IMentorLessonPlan,
} from "@/sanity/pages/PageMentorTemplate";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  DialogTitle,
} from "@/shadcn/ui/dialog";
import arrowRightIcon from "@/public/mentor/icon-arrow-right.svg";

interface LessonPlanDialogProps {
  triggerLabel: string;
  title?: string;
  lessons?: IMentorLesson[];
  plans?: IMentorLessonPlan[];
}

export default function LessonPlanDialog({
  triggerLabel,
  title = "Lesson plan",
  lessons,
  plans,
}: LessonPlanDialogProps) {
  const courses = plans?.filter((plan) => plan.lessons?.length) ?? [];
  const items = courses.length
    ? courses
    : lessons?.length
      ? [{ _key: "legacy", title, lessons }]
      : [];
  const [active, setActive] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const keys = items.map((item, index) => item._key ?? String(index));
  const selected = keys.includes(active) ? active : keys[0];

  if (!items.length) return null;

  return (
    <Dialog
      onOpenChange={(open) => {
        if (open) setActive(keys[0]);
      }}
    >
      <DialogTrigger ref={triggerRef} className="flex min-h-[5.5rem] items-center gap-[0.75rem] border-b border-white font-geist text-[2rem] font-medium leading-[3rem] text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#98D8FF]">
        {triggerLabel}
        <Image src={arrowRightIcon} alt="" className="h-[2rem] w-[2rem]" />
      </DialogTrigger>
      <DialogContent
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          closeRef.current?.focus({ preventScroll: true });
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          triggerRef.current?.focus({ preventScroll: true });
        }}
        overlayClassName="bg-black/70 backdrop-blur-[0.90625rem] ease-in-out data-[state=open]:duration-500 data-[state=closed]:duration-300 motion-reduce:animate-none"
        className="flex max-h-[calc(100dvh-4rem)] !w-[calc(100%-4rem)] flex-col overflow-clip rounded-[1.5rem] border border-white/[0.14] bg-[#111111] p-0 ease-in-out data-[state=open]:duration-500 data-[state=closed]:duration-300 ![--tw-enter-scale:1] ![--tw-enter-translate-x:-50%] ![--tw-enter-translate-y:-50%] ![--tw-exit-scale:1] ![--tw-exit-translate-x:-50%] ![--tw-exit-translate-y:-50%] motion-reduce:animate-none lg:!w-[64rem] [&>button]:hidden"
      >
          <div className="flex shrink-0 items-center justify-between px-[2.5rem] pt-[3rem] pb-[2rem] lg:px-[3rem]">
            <DialogTitle className="m-0 font-news text-[4rem] font-normal !leading-[4rem] tracking-[-0.12rem] text-white">
              {title}
            </DialogTitle>
            <DialogClose
              ref={closeRef}
              aria-label="Close lesson plan"
              className="flex h-[5.5rem] w-[5.5rem] shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#98D8FF]"
            >
              <svg
                viewBox="0 0 14 14"
                fill="none"
                aria-hidden
                className="h-[1.75rem] w-[1.75rem]"
              >
                <path
                  d="M1 1l12 12M13 1L1 13"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
            </DialogClose>
          </div>
          <Tabs.Root
            value={selected}
            onValueChange={setActive}
            className="flex min-h-0 flex-col"
          >
            {items.length > 1 && (
              <Tabs.List
                aria-label="Course lesson plans"
                className="flex shrink-0 flex-col gap-[1rem] px-[2.5rem] pb-[1rem] lg:flex-row lg:flex-wrap lg:px-[3rem]"
              >
                {items.map((course, index) => (
                  <Tabs.Trigger
                    key={keys[index]}
                    value={keys[index]}
                    className="min-h-[5.5rem] rounded-[1rem] border border-white/[0.08] px-[1.75rem] py-[1rem] font-geist text-[1.75rem] font-medium text-white/60 data-[state=active]:border-white/[0.16] data-[state=active]:bg-white/10 data-[state=active]:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#98D8FF]"
                  >
                    {course.title}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>
            )}
            {items.map((course, index) => (
              <Tabs.Content
                key={keys[index]}
                value={keys[index]}
                className="min-h-0 overflow-y-auto overscroll-contain px-[2.5rem] pt-[1rem] pb-[3rem] focus-visible:outline-none lg:px-[3rem] lg:pt-[1.5rem]"
              >
                <ol className="m-0 list-none p-0">
                  {course.lessons?.map((lesson, lessonIndex) => (
                    <li
                      key={lesson._key ?? lessonIndex}
                      className="font-geist text-[2rem] leading-[2.75rem]"
                    >
                      {lesson.moduleTitle && (
                        <h3
                          className={`m-0 pb-[0.5rem] font-geist text-[1.75rem] font-medium leading-[2.5rem] text-white/50 ${lessonIndex === 0 ? "pt-[1rem]" : "pt-[3.5rem]"}`}
                        >
                          {lesson.moduleTitle}
                        </h3>
                      )}
                      <div
                        className={`flex items-center gap-[1.5rem] border-white/10 py-[1.875rem] lg:gap-[2rem] lg:py-[2rem] ${lessonIndex === 0 && !lesson.moduleTitle ? "" : "border-t"}`}
                      >
                        <span className="w-[2.75rem] shrink-0 text-[#98D8FF] lg:w-[3.5rem]">
                          {lessonIndex + 1}
                        </span>
                        <span className="min-w-0 flex-1 font-medium text-white">
                          {lesson.title}
                        </span>
                        {lesson.duration && (
                          <span className="shrink-0 text-white/50">
                            {lesson.duration}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </Tabs.Content>
            ))}
          </Tabs.Root>
      </DialogContent>
    </Dialog>
  );
}
